// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { createSeamBridgeAdapter, } from "./seam-bridge-adapter.js";
import { parseWasmSquareDecision } from "./squares.js";
// ── SafetyKernel — the high-level API ────────────────────────
/**
 * Typed, theorem-grouped API over a `WasmKernel` backend.
 *
 * This is what application code should use. The adapter enforces
 * branded types at the TypeScript level while delegating enforcement
 * to the underlying WASM module.
 */
export class SafetyKernel {
    wasm;
    resConsume;
    resRelease;
    permit;
    separate;
    dataOrigin;
    dataReach;
    precommit;
    invEstablish;
    invPreserve;
    compose;
    seams;
    squares;
    constructor(wasm) {
        this.wasm = wasm;
        const w = wasm;
        this.resConsume = {
            allocate: (label) => w.resource_allocate(label),
            consume: (handle) => w.resource_consume(handle),
            isLive: (handle) => w.resource_is_live(handle),
            liveCount: () => w.resource_live_count(),
        };
        this.resRelease = {
            create: (label) => w.managed_create(label),
            release: (id) => w.managed_release(id),
            isReleased: (id) => w.managed_is_released(id),
            useAndRelease: (id) => w.managed_use_and_release(id),
        };
        this.permit = {
            createBoundary: (auth) => w.permit_create_boundary(auth),
            grant: (b, s, o) => w.permit_grant(b, s, o),
            validate: (c) => w.permit_validate(c),
            revoke: (c) => w.permit_revoke(c),
            revokeBoundary: (b) => w.permit_revoke_boundary(b),
        };
        this.separate = {
            asData: (v) => w.separate_as_data(v),
            asInstruction: (v) => w.separate_as_instruction(v),
            safeQuery: (t, p) => w.separate_safe_query(t, p),
        };
        this.dataOrigin = {
            brand: (v, l) => w.origin_brand(v, l),
            verify: (b) => w.origin_verify(b),
            checkSource: (b, e) => w.origin_check_source(b, e),
        };
        this.dataReach = {
            classify: (v, l) => w.reach_classify(v, l),
            declassify: (b, t) => w.reach_declassify(b, t),
            level: (b) => w.reach_level(b),
        };
        this.precommit = {
            create: (d) => w.precommit_create(d),
            commit: (h) => w.precommit_commit(h),
            cancel: (h) => w.precommit_cancel(h),
            state: (h) => w.precommit_state(h),
            description: (h) => w.precommit_description(h),
        };
        this.invEstablish = {
            createBuilder: () => w.inv_builder_create(),
            addCheck: (b, c) => w.inv_builder_add_check(b, c),
            establish: (b, s) => w.inv_establish(b, s),
            stateName: (s) => w.inv_state_name(s),
        };
        this.invPreserve = {
            createMachine: (s) => w.inv_machine_create(s),
            allowTransition: (m, f, t) => w.inv_machine_allow_transition(m, f, t),
            transition: (m, s) => w.inv_machine_transition(m, s),
            currentState: (m) => w.inv_machine_current_state(m),
            transitionCount: (m) => w.inv_machine_transition_count(m),
        };
        this.compose = {
            registerOperation: (n, i, o) => w.compose_register_operation(n, i, o),
            chain: (f, s) => w.compose_chain(f, s),
            validateChain: (c) => w.compose_validate_chain(c),
        };
        this.seams = createSeamBridgeAdapter(w);
        this.squares = {
            decide: (theoremCode, generatorCode) => parseWasmSquareDecision(w.square_decide(theoremCode, generatorCode)),
            isForbidden: (theoremCode, generatorCode) => w.square_is_forbidden(theoremCode, generatorCode),
        };
    }
    /** CSK version from the backend. */
    version() {
        return this.wasm.csk_version();
    }
    /** List of available theorems. */
    theorems() {
        return this.wasm.csk_available_theorems().split("\n");
    }
}
//# sourceMappingURL=adapter.js.map